import { PlanHistoryDto, StatusHistoryDto } from '@qse/api'
import { StatusChip, TableComponent } from '@qse/ui'
import { Funnel } from 'lucide-react'

interface HistoryEvent {
  id: string
  date: string
  fromValue?: string
  toValue?: string
  fromPlan?: string
  toPlan?: string
  planStorageGB?: number
  planApiLimit?: number
}

interface HistoryTableProps {
  data: StatusHistoryDto[] | PlanHistoryDto[]
  type: 'status' | 'plan'
  currentValue?: string
}

// Define custom column type for history tables
interface HistoryColumn {
  name: string
  uid: 'date' | 'fromValue' | 'toValue' | 'fromPlan' | 'toPlan'
  sortable?: boolean
}

// Define columns for the table
const statusColumns: HistoryColumn[] = [
  { name: 'Date', uid: 'date', sortable: true },
  { name: 'From Status', uid: 'fromValue' },
  { name: 'To Status', uid: 'toValue' },
]

const planColumns: HistoryColumn[] = [
  { name: 'Date', uid: 'date', sortable: true },
  { name: 'From Plan', uid: 'fromPlan' },
  { name: 'To Plan', uid: 'toPlan' },
]

export const HistoryTable = ({ data, type, currentValue }: HistoryTableProps) => {
  // Process data to detect changes and convert to HistoryEvent format
  const processData = (rawData: StatusHistoryDto[] | PlanHistoryDto[]) => {
    if (!rawData || rawData.length === 0) return []

    const changes: HistoryEvent[] = []

    for (let i = 0; i < rawData.length - 1; i++) {
      const current = rawData[i]
      const next = rawData[i + 1]

      if (type === 'status') {
        const currentStatus = current as StatusHistoryDto
        const nextStatus = next as StatusHistoryDto

        if (currentStatus.value !== nextStatus.value) {
          changes.push({
            id: `status-${nextStatus.date}-${i}`,
            date: nextStatus.date,
            fromValue: currentStatus.value,
            toValue: nextStatus.value,
          })
        }
      } else {
        const currentPlan = current as PlanHistoryDto
        const nextPlan = next as PlanHistoryDto

        if (currentPlan.planName !== nextPlan.planName) {
          // Handle both planStorageGB and planStorageBytes from API
          const storageGB =
            (nextPlan as any).planStorageGB ||
            ((nextPlan as any).planStorageBytes
              ? (nextPlan as any).planStorageBytes / (1024 * 1024 * 1024)
              : 0)

          changes.push({
            id: `plan-${nextPlan.date}-${i}`,
            date: nextPlan.date,
            fromPlan: currentPlan.planName,
            toPlan: nextPlan.planName,
            planStorageGB: storageGB,
            planApiLimit: nextPlan.planApiLimit,
          })
        }
      }
    }

    // Sort changes by date (newest first for display)
    return changes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  const processedData = processData(data)

  if (processedData.length === 0) {
    const noChangeMessage =
      type === 'status'
        ? `No status changes during selected period${currentValue ? ` (Current: ${currentValue})` : ''}`
        : `No plan changes during selected period${currentValue ? ` (Current: ${currentValue} Plan)` : ''}`

    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 dark:bg-[#2A2A2A] flex items-center justify-center">
          <Funnel />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Changes Found</h3>
        <p className="text-sm text-gray-500 dark:text-[#AFAFAF] text-center max-w-lg leading-relaxed">
          {noChangeMessage}
        </p>
      </div>
    )
  }

  // Render cell function for status history
  const renderStatusCell = (event: HistoryEvent, columnKey: HistoryColumn['uid']) => {
    switch (columnKey) {
      case 'date':
        return (
          <span className="text-sm text-gray-900 dark:text-white">
            {new Date(event.date).toLocaleDateString()}
          </span>
        )
      case 'fromValue':
        return event.fromValue ? (
          <StatusChip status={event.fromValue} />
        ) : (
          <span className="text-sm text-gray-500 dark:text-[#AFAFAF]">-</span>
        )
      case 'toValue':
        return event.toValue ? (
          <StatusChip status={event.toValue} />
        ) : (
          <span className="text-sm text-gray-500 dark:text-[#AFAFAF]">-</span>
        )
      default:
        return null
    }
  }

  // Render cell function for plan history
  const renderPlanCell = (event: HistoryEvent, columnKey: HistoryColumn['uid']) => {
    switch (columnKey) {
      case 'date':
        return (
          <span className="text-sm text-gray-900 dark:text-white">
            {new Date(event.date).toLocaleDateString()}
          </span>
        )
      case 'fromPlan':
        return (
          <span className="text-sm text-gray-900 dark:text-white">{event.fromPlan || '-'}</span>
        )
      case 'toPlan':
        return <span className="text-sm text-gray-900 dark:text-white">{event.toPlan || '-'}</span>
      default:
        return null
    }
  }

  if (type === 'status') {
    return (
      <TableComponent
        columns={statusColumns as any}
        customRenderCell={renderStatusCell as any}
        data={processedData}
        disableLocalPagination={true}
        high="h-[200px]"
        initialVisibleColumns={['date', 'fromValue', 'toValue']}
        isLoading={false}
        selectionMode="none"
        showBottomContent={false}
        showTopContent={false}
        trClassName="border-b dark:border-[#2A2A2A] border-[#2A2A2A]/10 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2A2A2A]/20"
      />
    )
  }

  // Plan history table
  return (
    <TableComponent
      columns={planColumns as any}
      customRenderCell={renderPlanCell as any}
      data={processedData}
      disableLocalPagination={true}
      high="h-[200px]"
      initialVisibleColumns={['date', 'fromPlan', 'toPlan']}
      isLoading={false}
      selectionMode="none"
      showBottomContent={false}
      showTopContent={false}
      trClassName="border-b dark:border-[#2A2A2A] border-[#2A2A2A]/10 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2A2A2A]/20"
    />
  )
}
